import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'shared-ui';

import { fetchDodDlq, fetchDodPromql, fetchDodQueues, fetchDodTimeseries } from 'api/dod/dod.v2';
import { DodTimeseriesLabelName, DodTimeseriesSeries } from 'api/dod/dod.v2.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { AppRoute } from 'routing/AppRoute.enum';
import { BarDatum } from '../components/barPanel/BarPanel.types';
import { dodReaderLabel } from '../overviewTab/dodFormat';
import { dodCommandLabel } from '../components/dodCommands';
import { rangeStepSeconds, useDodFilters } from '../hooks/useDodFilters/useDodFilters';
import { useDodOutlet } from '../hooks/useDodOutlet/useDodOutlet';

import { RetriesTab } from './RetriesTab';
import { RetriesQueueRow } from './RetriesTab.types';
import {
  PromqlQuantile,
  deliveryQuantileQuery,
  deriveAttemptHistogram,
  promqlToSeries,
  sendP95PerCommandQuery,
} from './retries.helpers';

/** One page of each list is enough to characterise the distribution without a second round trip. */
const SAMPLE_PAGE_SIZE = 200;

const RETRIES_METRIC = 'dod_order_retries_total';

const RETRIES_GROUP_BY: DodTimeseriesLabelName[] = ['command'];
const DEFERRED_METRIC = 'dod_drain_deferred_total';

/**
 * `dim` carries the pre-check reason for this counter (rcp-not-connected, tkd-not-ok, cooldown),
 * so the panel splits per reason exactly as §4.5 asks. A backend without `dim` returns no such
 * label and the panel falls back to one undifferentiated series, saying so in its caption.
 */
const DEFERRED_GROUP_BY: DodTimeseriesLabelName[] = ['dim'];

const QUANTILES: PromqlQuantile[] = [0.5, 0.95, 0.99];

export const RetriesTabContainer = (): JSX.Element => {
  const { formatMessage } = useLocale();
  const navigate = useNavigate();
  const { capabilities, refreshToken, setToolbarOptions } = useDodOutlet();
  const { range, refreshMs, filters, resolveRange, queryString } = useDodFilters();

  const stepSeconds = rangeStepSeconds(range);
  const { fromTs, toTs } = resolveRange();

  const {
    data: queuesPage,
    isLoading: isQueuesLoading,
    isError: isQueuesError,
  } = useQuery([QueryKeyEnum.DOD_QUEUES, 'retries', filters.rcp, refreshToken], fetchDodQueues, {
    handleErrorManually: true,
    args: { page: 1, pageSize: SAMPLE_PAGE_SIZE, ...(filters.rcp ? { rcpId: filters.rcp } : {}) },
    refetchInterval: refreshMs,
  });

  const {
    data: dlqPage,
    isLoading: isDlqLoading,
    isError: isDlqError,
  } = useQuery([QueryKeyEnum.DOD_DLQ, 'retries', filters.rcp, range, refreshToken], fetchDodDlq, {
    handleErrorManually: true,
    args: {
      page: 1,
      pageSize: SAMPLE_PAGE_SIZE,
      from: fromTs,
      to: toTs,
      ...(filters.rcp ? { rcpId: filters.rcp } : {}),
    },
    refetchInterval: refreshMs,
  });

  const { data: retries, isLoading: isRetriesLoading } = useQuery(
    [QueryKeyEnum.DOD_TIMESERIES, RETRIES_METRIC, range, filters.rcp, refreshToken],
    fetchDodTimeseries,
    {
      handleErrorManually: true,
      args: {
        metric: RETRIES_METRIC,
        from: fromTs,
        to: toTs,
        step: stepSeconds * 1000,
        groupBy: RETRIES_GROUP_BY,
        ...(filters.rcp ? { rcpId: filters.rcp } : {}),
      },
      enabled: capabilities.timeseries,
      refetchInterval: refreshMs,
    },
  );

  const { data: deferred, isLoading: isDeferredLoading } = useQuery(
    [QueryKeyEnum.DOD_TIMESERIES, DEFERRED_METRIC, range, refreshToken],
    fetchDodTimeseries,
    {
      handleErrorManually: true,
      args: { metric: DEFERRED_METRIC, from: fromTs, to: toTs, step: stepSeconds * 1000, groupBy: DEFERRED_GROUP_BY },
      enabled: capabilities.timeseries,
      refetchInterval: refreshMs,
    },
  );

  const promqlArgs = {
    start: Math.floor(fromTs / 1000),
    end: Math.floor(toTs / 1000),
    step: stepSeconds,
  };

  const deliveryP50 = useQuery([QueryKeyEnum.DOD_PROMQL, 'delivery-p50', range, refreshToken], fetchDodPromql, {
    handleErrorManually: true,
    args: { ...promqlArgs, query: deliveryQuantileQuery(QUANTILES[0], stepSeconds) },
    enabled: capabilities.prometheus,
    refetchInterval: refreshMs,
  });

  const deliveryP95 = useQuery([QueryKeyEnum.DOD_PROMQL, 'delivery-p95', range, refreshToken], fetchDodPromql, {
    handleErrorManually: true,
    args: { ...promqlArgs, query: deliveryQuantileQuery(QUANTILES[1], stepSeconds) },
    enabled: capabilities.prometheus,
    refetchInterval: refreshMs,
  });

  const deliveryP99 = useQuery([QueryKeyEnum.DOD_PROMQL, 'delivery-p99', range, refreshToken], fetchDodPromql, {
    handleErrorManually: true,
    args: { ...promqlArgs, query: deliveryQuantileQuery(QUANTILES[2], stepSeconds) },
    enabled: capabilities.prometheus,
    refetchInterval: refreshMs,
  });

  const sendP95 = useQuery([QueryKeyEnum.DOD_PROMQL, 'send-p95', range, refreshToken], fetchDodPromql, {
    handleErrorManually: true,
    args: { ...promqlArgs, query: sendP95PerCommandQuery(stepSeconds) },
    enabled: capabilities.prometheus,
    refetchInterval: refreshMs,
  });

  const attemptBuckets = useMemo(
    () =>
      deriveAttemptHistogram({
        dlqRetryCounts: (dlqPage?.items ?? []).map((entry) => entry.retryCount),
        queueRetryMaxes: (queuesPage?.items ?? []).map((queue) => queue.retryCountMax),
      }),
    [dlqPage, queuesPage],
  );

  const attemptBars = useMemo<BarDatum[]>(
    () =>
      attemptBuckets.map((bucket) => ({
        id: String(bucket.attempt),
        label: formatMessage({ id: AppMessages['dod.retries.attempts.bucket'] }, { attempt: bucket.attempt }),
        value: bucket.total,
      })),
    [attemptBuckets, formatMessage],
  );

  const retriesSeries = useMemo<DodTimeseriesSeries[]>(() => retries?.series ?? [], [retries]);

  const retriesSeriesLabels = useMemo(
    () =>
      retriesSeries.reduce<Record<string, string>>((labels, series) => {
        const command = series.labels.command;

        return { ...labels, [series.key]: command ? dodCommandLabel(command.padStart(2, '0')) : series.key };
      }, {}),
    [retriesSeries],
  );

  const deferredSeries = useMemo<DodTimeseriesSeries[]>(() => deferred?.series ?? [], [deferred]);

  const hasDeferredReasons = useMemo(() => deferredSeries.some((series) => !!series.labels.dim), [deferredSeries]);

  const deferredSeriesLabels = useMemo(
    () =>
      deferredSeries.reduce<Record<string, string>>(
        (labels, series) => ({ ...labels, [series.key]: series.labels.dim ?? series.key }),
        {},
      ),
    [deferredSeries],
  );

  const deliveryQuantileSeries = useMemo<DodTimeseriesSeries[]>(
    () => [
      ...promqlToSeries(deliveryP50.data, () => 'p50'),
      ...promqlToSeries(deliveryP95.data, () => 'p95'),
      ...promqlToSeries(deliveryP99.data, () => 'p99'),
    ],
    [deliveryP50.data, deliveryP95.data, deliveryP99.data],
  );

  const sendQuantileSeries = useMemo<DodTimeseriesSeries[]>(
    () => promqlToSeries(sendP95.data, (metric) => metric.command ?? 'total'),
    [sendP95.data],
  );

  const sendQuantileLabels = useMemo(
    () =>
      sendQuantileSeries.reduce<Record<string, string>>(
        (labels, series) => ({ ...labels, [series.key]: dodCommandLabel(String(series.key).padStart(2, '0')) }),
        {},
      ),
    [sendQuantileSeries],
  );

  /** Highest retry count first: the point of the table is which orders are burning their budget. */
  const rows = useMemo<RetriesQueueRow[]>(
    () =>
      (queuesPage?.items ?? [])
        .map((queue) => ({
          id: `${queue.rcpId}:${queue.nlk}`,
          rcpId: queue.rcpId,
          rcpLabel: queue.rcpNazwa ?? queue.rcpId,
          nlk: queue.nlk,
          tkdLabel: dodReaderLabel(queue.tkdNazwa, queue.nlk),
          state: queue.status,
          retryCountMax: queue.retryCountMax,
          depth: queue.depth,
          dlqDepth: queue.dlqDepth,
          lastError: queue.lastError ?? '',
        }))
        .filter((row) => row.retryCountMax > 0)
        .sort((left, right) => right.retryCountMax - left.retryCountMax),
    [queuesPage],
  );

  /** RCP and TKD options for the shared toolbar, taken from the queues this tab already loaded. */
  useEffect(() => {
    const byRcp = new Map(rows.map((row) => [row.rcpId, row.rcpLabel]));
    const byTkd = new Map(rows.map((row) => [String(row.nlk), row.tkdLabel]));

    setToolbarOptions({
      rcp: Array.from(byRcp, ([value, label]) => ({ value, label })),
      tkd: Array.from(byTkd, ([value, label]) => ({ value, label })),
    });
  }, [rows, setToolbarOptions]);

  /**
   * Both links carry the cross-tab filters forward, so the queue drawer and the dead-letter table
   * open on the same TKD and the same time window the operator was already looking at.
   */
  const withTkd = useCallback(
    (route: AppRoute, row: RetriesQueueRow): string => {
      const params = new URLSearchParams(queryString);

      // The Queues tab keys its details panel on `queue=`; `rcp`/`nlk` are the toolbar filters
      // and would narrow that tab's table to one server instead of opening the panel.
      if (route === AppRoute.dodQueues) {
        params.set('queue', `${row.rcpId}:${row.nlk}`);
      } else {
        params.set('rcp', row.rcpId);
        params.set('nlk', String(row.nlk));
      }

      return `${route}?${params.toString()}`;
    },
    [queryString],
  );

  return (
    <RetriesTab
      attemptBuckets={attemptBuckets}
      attemptBars={attemptBars}
      isAttemptsLoading={isDlqLoading || isQueuesLoading}
      retriesSeries={retriesSeries}
      retriesSeriesLabels={retriesSeriesLabels}
      isRetriesLoading={isRetriesLoading}
      deferredSeries={deferredSeries}
      deferredSeriesLabels={deferredSeriesLabels}
      hasDeferredReasons={hasDeferredReasons}
      isDeferredLoading={isDeferredLoading}
      hasPrometheus={capabilities.prometheus}
      deliveryQuantileSeries={deliveryQuantileSeries}
      deliveryQuantileLabels={{
        p50: formatMessage({ id: AppMessages['dod.retries.quantile.p50'] }),
        p95: formatMessage({ id: AppMessages['dod.retries.quantile.p95'] }),
        p99: formatMessage({ id: AppMessages['dod.retries.quantile.p99'] }),
      }}
      sendQuantileSeries={sendQuantileSeries}
      sendQuantileLabels={sendQuantileLabels}
      isQuantilesLoading={deliveryP95.isLoading || sendP95.isLoading}
      rows={rows}
      isRowsLoading={isQueuesLoading}
      isError={isQueuesError || isDlqError}
      onOpenQueue={(row) => navigate(withTkd(AppRoute.dodQueues, row))}
    />
  );
};
